import React, { useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Button,
  Stack,
  Paper,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { RichTextReadOnly } from "mui-tiptap";
import {
  AccountManagementComment,
  AccountManagementCommentObjectType,
  AccountManagementObjectType,
  AccountManagementTask,
  AccountManagementTaskStatus,
} from "../../../models/AccountManagement";
import {
  useCreateAccountManagementComment,
  useUpdateAccountManagementComment,
  useCsmTalentComments,
} from "../../../hooks/useAccountManagement";
import HtmlEditor, { getExtensions } from "../../UI/HtmlEditor";

interface CommentSectionProps {
  projectId: number;
  comments: AccountManagementComment[];
  tasks: AccountManagementTask[];
  isLead: boolean;
  currentUserId: number;
  clientUsers: { id: number; firstName: string; lastName: string }[];
  freelancerUser?: { id: number; firstName: string; lastName: string };
}

/**
 * Maps a comment object type to the corresponding task object type.
 * Used to look up the AM task linked to a comment for status checks.
 */
const mapCommentToTaskType = (
  commentType: AccountManagementCommentObjectType
): AccountManagementObjectType | null => {
  switch (commentType) {
    case AccountManagementCommentObjectType.FeedbackClient:
    case AccountManagementCommentObjectType.FeedbackTalent:
      return AccountManagementObjectType.Feedback;
    case AccountManagementCommentObjectType.PreBilanUpsell:
      return AccountManagementObjectType.PreBilanUpsell;
    case AccountManagementCommentObjectType.QuarterlyReviewClient:
    case AccountManagementCommentObjectType.QuarterlyReviewTalent:
      return AccountManagementObjectType.QuarterlyReview;
    case AccountManagementCommentObjectType.SatisfactionClient:
    case AccountManagementCommentObjectType.SatisfactionTalent:
      return AccountManagementObjectType.Satisfaction;
    case AccountManagementCommentObjectType.BilanClient:
    case AccountManagementCommentObjectType.BilanTalent:
      return AccountManagementObjectType.Bilan;
    default:
      return null;
  }
};

// i18n key suffix for each comment object type, looked up under `accountManagement.commentObjectTypes`.
const COMMENT_OBJECT_TYPE_I18N_KEYS: Record<AccountManagementCommentObjectType, string> = {
  [AccountManagementCommentObjectType.FeedbackClient]: "feedbackClient",
  [AccountManagementCommentObjectType.FeedbackTalent]: "feedbackTalent",
  [AccountManagementCommentObjectType.PreBilanUpsell]: "preBilanUpsell",
  [AccountManagementCommentObjectType.QuarterlyReviewClient]: "quarterlyReviewClient",
  [AccountManagementCommentObjectType.QuarterlyReviewTalent]: "quarterlyReviewTalent",
  [AccountManagementCommentObjectType.SatisfactionClient]: "satisfactionClient",
  [AccountManagementCommentObjectType.SatisfactionTalent]: "satisfactionTalent",
  [AccountManagementCommentObjectType.BilanClient]: "bilanClient",
  [AccountManagementCommentObjectType.BilanTalent]: "bilanTalent",
  [AccountManagementCommentObjectType.CsmTalentComment]: "csmTalentComment",
  [AccountManagementCommentObjectType.Other]: "other",
};

const getCommentObjectTypeLabel = (
  type: AccountManagementCommentObjectType,
  t: TFunction
): string => t(`accountManagement.commentObjectTypes.${COMMENT_OBJECT_TYPE_I18N_KEYS[type]}`);

const TALENT_OBJECT_TYPES = [
  AccountManagementCommentObjectType.FeedbackTalent,
  AccountManagementCommentObjectType.QuarterlyReviewTalent,
  AccountManagementCommentObjectType.SatisfactionTalent,
  AccountManagementCommentObjectType.BilanTalent,
  AccountManagementCommentObjectType.CsmTalentComment,
];

const isTalentObjectTypeFn = (type: AccountManagementCommentObjectType) =>
  TALENT_OBJECT_TYPES.includes(type);

// Object types that carry a sequence number (one comment per quarterly task).
const SEQUENCED_COMMENT_TYPES = new Set<AccountManagementCommentObjectType>([
  AccountManagementCommentObjectType.QuarterlyReviewClient,
  AccountManagementCommentObjectType.QuarterlyReviewTalent,
]);

// Comment object types intentionally hidden from the dropdown.
// PO requirement: "Suivi trimestriel" tasks are addressed to clients only — no
// counterpart for the talent — so the Talent variant doesn't make sense as a
// comment option. We keep the value in the enum (and i18n key) so any historical
// comment of that type still renders correctly on the existing-comments list.
const HIDDEN_COMMENT_OBJECT_TYPES = new Set<AccountManagementCommentObjectType>([
  AccountManagementCommentObjectType.QuarterlyReviewTalent,
]);

interface CommentEntry {
  objectType: AccountManagementCommentObjectType;
  /** null for non-sequenced types (most). Set for QuarterlyReviewClient/Talent. */
  sequenceNumber: number | null;
  /** Stable identifier used as Select value + React key. */
  key: string;
  /** Label shown in dropdown and on the comment chip. */
  label: string;
}

const buildEntryKey = (
  objectType: AccountManagementCommentObjectType,
  sequenceNumber: number | null
) => (sequenceNumber != null ? `${objectType}:${sequenceNumber}` : objectType);

const buildEntryLabel = (
  objectType: AccountManagementCommentObjectType,
  sequenceNumber: number | null,
  t: TFunction
) => {
  const base = getCommentObjectTypeLabel(objectType, t);
  return sequenceNumber != null ? `${base} - ${sequenceNumber}` : base;
};

const AccountManagementCommentSection: React.FC<CommentSectionProps> = ({
  projectId,
  comments,
  tasks,
  isLead,
  currentUserId,
  clientUsers,
  freelancerUser,
}) => {
  const { t, i18n } = useTranslation();
  const createComment = useCreateAccountManagementComment();
  const updateComment = useUpdateAccountManagementComment();
  const { data: csmTalentNotes = [] } = useCsmTalentComments(projectId);

  /**
   * Compute available users for a given comment entry (objectType + optional sequenceNumber).
   *
   * Three flavors of comment types:
   *
   * 1. "Direct" comments — the AM task targets the same user as the comment recipient.
   *    For sequenced types (QuarterlyReviewClient), the entry's sequence is used to
   *    pinpoint the exact task (e.g. QR seq=2 for client A).
   *    → per-user gating: a user is excluded only if HIS OWN task is still Planned.
   *
   * 2. "Indirect" comments — the AM task targets someone else (Bilan task targets CSM Lead,
   *    QuarterlyReview targets clients but we look at the talent comment).
   *    For sequenced types (QuarterlyReviewTalent), the entry's sequence is used.
   *    → whole-type gating: as long as the corresponding task (with same sequence) is
   *      still Planned, no comment is allowed.
   *
   * 3. "No task" comments (CsmTalentComment, Other) — never gated by tasks.
   *
   * In all cases, users already commented for this (objectType, sequenceNumber) tuple
   * are filtered out.
   */
  const computeAvailableUsers = (entry: CommentEntry) => {
    const { objectType, sequenceNumber } = entry;
    const isTalent = isTalentObjectTypeFn(objectType);
    const base = isTalent
      ? freelancerUser
        ? [freelancerUser]
        : []
      : clientUsers;

    if (base.length === 0) return [];

    // Per-(objectType, sequence) "already commented" filter — for QR types this allows
    // multiple comments for the same user (one per quarter).
    const alreadyCommented = new Set(
      comments
        .filter(
          (c) =>
            c.objectType === objectType &&
            (c.sequenceNumber ?? null) === sequenceNumber
        )
        .flatMap((c) => c.targetUsers.map((tu) => tu.userId))
    );

    const taskType = mapCommentToTaskType(objectType);
    if (taskType === null) {
      // No AM task linked → only filter by alreadyCommented (CsmTalentComment, Other)
      return base.filter((u) => !alreadyCommented.has(u.id));
    }

    // For sequenced types, narrow tasks to the matching sequence.
    const tasksOfType = tasks.filter(
      (t) =>
        t.objectType === taskType &&
        (sequenceNumber == null || t.sequenceNumber === sequenceNumber)
    );
    const baseIds = new Set(base.map((u) => u.id));

    // Does at least one task of this type target a user in `base`?
    //   yes → "direct" comment, gate per-user
    //   no  → "indirect" comment (e.g. Bilan task targets CSM Lead, not the client),
    //         gate the whole type by any Planned task on the project
    const hasDirectTaskForBase = tasksOfType.some((t) =>
      baseIds.has(t.targetUserId)
    );

    if (hasDirectTaskForBase) {
      const blockedIds = new Set(
        tasksOfType
          .filter(
            (t) =>
              t.status === AccountManagementTaskStatus.Planned &&
              baseIds.has(t.targetUserId)
          )
          .map((t) => t.targetUserId)
      );
      return base.filter(
        (u) => !alreadyCommented.has(u.id) && !blockedIds.has(u.id)
      );
    }

    // Indirect: any Planned task of this (type, sequence) blocks the whole entry
    const isBlocked = tasksOfType.some(
      (t) => t.status === AccountManagementTaskStatus.Planned
    );
    if (isBlocked) return [];

    return base.filter((u) => !alreadyCommented.has(u.id));
  };

  // Build all possible entries: 1 per non-sequenced type + 1 per QR task sequence.
  // The QR sequences are derived from the actual tasks on the project, so a 365-day
  // project with 3 QR tasks naturally produces 3 "Suivi trimestriel Client - N" entries.
  
  const allEntries: CommentEntry[] = React.useMemo(() => {
    const entries: CommentEntry[] = [];
    const allTypes = Object.keys(
      COMMENT_OBJECT_TYPE_I18N_KEYS
    ) as AccountManagementCommentObjectType[];

    // QR sequences present on this project (e.g. [1, 2, 3] for a long project).
    const qrSequences = Array.from(
      new Set(
        tasks
          .filter((task) => task.objectType === AccountManagementObjectType.QuarterlyReview)
          .map((task) => task.sequenceNumber)
          .filter((s): s is number => s != null)
      )
    ).sort((a, b) => a - b);

    // Set of task object types actually generated for this project — used to skip
    // comment entries whose linked AM task wasn't generated (cf. PO feedback).
    const presentTaskTypes = new Set(tasks.map((task) => task.objectType));

    for (const type of allTypes) {
      // Skip PO-hidden types regardless of any other logic (see HIDDEN_COMMENT_OBJECT_TYPES).
      if (HIDDEN_COMMENT_OBJECT_TYPES.has(type)) continue;

      if (SEQUENCED_COMMENT_TYPES.has(type)) {
        // QR — already filtered: only sequences present on the project.
        for (const seq of qrSequences) {
          entries.push({
            objectType: type,
            sequenceNumber: seq,
            key: buildEntryKey(type, seq),
            label: buildEntryLabel(type, seq, t),
          });
        }
      } else {
        const linkedTaskType = mapCommentToTaskType(type);
        // Skip linked-but-unrepresented types. Types with no linked task (null)
        // — CsmTalentComment, Other — pass through unconditionally.
        if (linkedTaskType !== null && !presentTaskTypes.has(linkedTaskType)) {
          continue;
        }
        entries.push({
          objectType: type,
          sequenceNumber: null,
          key: buildEntryKey(type, null),
          label: buildEntryLabel(type, null, t),
        });
      }
    }
    return entries;
  }, [tasks, t]);

  // Filter entries that still have at least one selectable recipient.
  // Memoized: recomputed only when entries, comments, tasks or recipients change.
  const availableEntries = React.useMemo(
    () => allEntries.filter((entry) => computeAvailableUsers(entry).length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allEntries, comments, tasks, clientUsers, freelancerUser]
  );

  // Default to the first available entry; fall back to a stable known one.
  const defaultEntryKey =
    availableEntries[0]?.key ??
    buildEntryKey(AccountManagementCommentObjectType.FeedbackClient, null);

  const [selectedEntryKey, setSelectedEntryKey] = useState<string>(defaultEntryKey);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [content, setContent] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState("");

  const [composerResetKey, setComposerResetKey] = useState(0);
  const resetComposer = React.useCallback(() => {
    setContent("");
    setComposerResetKey((k) => k + 1);
  }, []);

  // Resolve the currently selected entry from its key.
  const selectedEntry: CommentEntry =
    allEntries.find((e) => e.key === selectedEntryKey) ??
    availableEntries[0] ?? {
      objectType: AccountManagementCommentObjectType.FeedbackClient,
      sequenceNumber: null,
      key: buildEntryKey(AccountManagementCommentObjectType.FeedbackClient, null),
      label: buildEntryLabel(AccountManagementCommentObjectType.FeedbackClient, null, t),
    };
  const selectedObjectType = selectedEntry.objectType;
  const selectedSequenceNumber = selectedEntry.sequenceNumber;

  // If the currently selected entry becomes unavailable (e.g. just commented all clients),
  // automatically switch to the first still-available entry.
  React.useEffect(() => {
    if (
      availableEntries.length > 0 &&
      !availableEntries.some((e) => e.key === selectedEntryKey)
    ) {
      setSelectedEntryKey(availableEntries[0].key);
    }
  }, [availableEntries, selectedEntryKey]);

  const isTalentObjectType = isTalentObjectTypeFn(selectedObjectType);

  React.useEffect(() => {
    resetComposer();
    if (isTalentObjectType && freelancerUser) {
      setSelectedUserIds([freelancerUser.id]);
    } else {
      setSelectedUserIds([]);
    }
  }, [selectedEntryKey, isTalentObjectType, freelancerUser, resetComposer]);

  const availableUsers = computeAvailableUsers(selectedEntry);
  const noAvailableUsers = availableUsers.length === 0;
  const noObjectTypesLeft = availableEntries.length === 0;

  const handleUserToggle = (userId: number) => {
    if (isTalentObjectType) return; // Talent is always pre-selected
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const isCsmTalentComment =
    selectedObjectType === AccountManagementCommentObjectType.CsmTalentComment;

  const handleSubmit = async () => {
    if (!content.trim()) return;

    // For talent-typed comments (including CsmTalentComment), the backend
    // auto-resolves the accepted freelancer if no targetUserIds is provided.
    // For client-typed comments, we require at least one selected client.
    let targetIds: number[];
    if (isCsmTalentComment || isTalentObjectType) {
      targetIds = freelancerUser ? [freelancerUser.id] : [];
      // Empty array is OK - backend will auto-resolve from project
    } else {
      targetIds = selectedUserIds;
      if (targetIds.length === 0) return;
    }

    await createComment.mutateAsync({
      projectId,
      data: {
        objectType: selectedObjectType,
        content,
        targetUserIds: targetIds,
        sequenceNumber: selectedSequenceNumber,
      },
    });

    // Wipe the composer for the next comment (mui-tiptap is uncontrolled —
    // we MUST bump composerResetKey, see resetComposer).
    resetComposer();
    setSelectedUserIds([]);
  };

  const handleUpdate = async (commentId: number) => {
    if (!editContent.trim()) return;

    await updateComment.mutateAsync({
      projectId,
      commentId,
      data: { content: editContent },
    });

    setEditingCommentId(null);
    setEditContent("");
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(i18n.language, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // (Recipient pre-selection on object change is handled by the draft-reset
  //  effect above — kept in a single useEffect so we don't write
  //  setSelectedUserIds twice on the same render.)

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          mb: 2,
          letterSpacing: "0.2px",
        }}
      >
        {t("accountManagement.relationshipTracking", "Suivi de la relation Client et Talent")}
      </Typography>

      {/* Comment form - Lead only */}
      {isLead && noObjectTypesLeft && (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            {t(
              "accountManagement.allObjectsCommented",
              "Tous les objets et destinataires possibles ont déjà été commentés. Utilisez le bouton « Modifier » sur un commentaire ci-dessous pour l'éditer."
            )}
          </Typography>
        </Paper>
      )}

      {isLead && !noObjectTypesLeft && (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            backgroundColor: "background.paper",
          }}
        >
          <Stack spacing={2}>
            <FormControl size="small" sx={{ maxWidth: 320 }}>
              <InputLabel>{t("accountManagement.objectType", "Objet")}</InputLabel>
              <Select
                value={selectedEntryKey}
                onChange={(e) => setSelectedEntryKey(e.target.value as string)}
                label={t("accountManagement.objectType", "Objet")}
              >
                {availableEntries.map((entry) => (
                  <MenuItem key={entry.key} value={entry.key}>
                    {entry.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("accountManagement.selectRecipients", "Destinataires")}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {availableUsers.map((user) => (
                  <Chip
                    key={user.id}
                    label={`${user.firstName} ${user.lastName}`}
                    onClick={() => handleUserToggle(user.id)}
                    color={
                      selectedUserIds.includes(user.id) || isTalentObjectType
                        ? "primary"
                        : "default"
                    }
                    variant={
                      selectedUserIds.includes(user.id) || isTalentObjectType
                        ? "filled"
                        : "outlined"
                    }
                    disabled={isTalentObjectType}
                  />
                ))}
              </Stack>
            </Box>

            {/* `key={composerResetKey}` forces a fresh remount of the editor
                whenever resetComposer() is called (after submit, cancel, or
                object change). The underlying mui-tiptap is uncontrolled and
                won't sync visually with `content` after mount otherwise. */}
            <HtmlEditor
              key={composerResetKey}
              content={content}
              onUpdate={({ editor }) => setContent(editor.getHTML())}
              placeholder={t("accountManagement.commentPlaceholder", "Saisissez votre commentaire...")}
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={
                  !content.trim() ||
                  // Client-targeted comments require at least one selected client
                  (selectedUserIds.length === 0 && !isTalentObjectType && !isCsmTalentComment) ||
                  // No available recipient (all already commented or task still Planned)
                  noAvailableUsers ||
                  createComment.isPending
                }
              >
                {t("accountManagement.save", "Enregistrer")}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  resetComposer();
                  setSelectedUserIds([]);
                }}
              >
                {t("accountManagement.cancel", "Annuler")}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* When CsmTalentComment is selected, show previous talent comments for this project */}
      {isCsmTalentComment && csmTalentNotes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
            {t(
              "accountManagement.previousCsmTalentComments",
              "Commentaires CSM sur le talent pr\u00e9c\u00e9dents"
            )}
          </Typography>
          <Stack spacing={1}>
            {csmTalentNotes.map((note) => (
              <Paper key={note.id} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {formatDate(note.createdAt)}
                  {" | "}
                  {note.authorFullName ??
                    t("accountManagement.unknownAuthor", "Auteur inconnu")}
                </Typography>
                <Box sx={{ "& p": { m: 0 } }}>
                  <RichTextReadOnly content={note.content} extensions={getExtensions()} />
                </Box>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Comments list - reverse chronological */}
      <Stack spacing={2}>
        {comments.map((comment) => (
          <Paper key={comment.id} variant="outlined" sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Box>
                <Chip
                  label={buildEntryLabel(
                    comment.objectType,
                    comment.sequenceNumber ?? null,
                    t
                  )}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 0.5 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {comment.targetUsers
                    .map((tu) => `${tu.user.firstName} ${tu.user.lastName}`)
                    .join(", ")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(comment.createdAt)}
                </Typography>
                {isLead && comment.authorUserId === currentUserId && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingCommentId(comment.id);
                      setEditContent(comment.content);
                    }}
                    title={t("accountManagement.editComment", "Modifier")}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {comment.authorUser.firstName} {comment.authorUser.lastName}
            </Typography>

            {editingCommentId === comment.id ? (
              <Box>
                <HtmlEditor
                  content={editContent}
                  onUpdate={({ editor }) => setEditContent(editor.getHTML())}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleUpdate(comment.id)}
                    disabled={updateComment.isPending}
                  >
                    {t("accountManagement.save", "Enregistrer")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setEditingCommentId(null)}
                  >
                    {t("accountManagement.cancel", "Annuler")}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <RichTextReadOnly content={comment.content} extensions={getExtensions()} />
            )}
          </Paper>
        ))}

        {comments.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            {t("accountManagement.noComments", "Aucun commentaire")}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default AccountManagementCommentSection;
